import { SearchOutlined } from '@ant-design/icons';
import { Button, Col, Input, Pagination, Row } from 'antd';
import generatePicker from 'antd/es/date-picker/generatePicker';
import type { RangeValue } from 'rc-picker/es/interface';
import { useCallback, useMemo } from 'react';

import { dateGenerateConfig } from 'app/components/fields/dateGenerateConfig';
import { formatDateWithoutHour } from 'app/utils/dates';
import { endOfISOWeek, startOfISOWeek } from 'app/utils/dayjs';

import styles from '../ExtranetBookings.module.scss';

const DatePicker = generatePicker<Date>(dateGenerateConfig);
const { RangePicker } = DatePicker;

type ExtranetBookingsHeaderProps = {
  searchValue: string;
  onSearchChange: (value: string) => void;
  beginning?: string;
  ending?: string;
  onDateChange: (beginning?: string, ending?: string) => void;
  onDownloadCsv?: () => void;
  isDownloading?: boolean;
  pagination?: {
    pageNumber: number;
    pageSize: number;
    pageCount: number;
    itemCount: number;
  };
  onPageChange?: (page: number) => void;
};

export const ExtranetBookingsHeader = ({
  searchValue,
  onSearchChange,
  beginning,
  ending,
  onDateChange,
  onDownloadCsv,
  isDownloading,
  pagination,
  onPageChange,
}: ExtranetBookingsHeaderProps) => {
  const dateRange = useMemo((): RangeValue<Date> => {
    if (!beginning || !ending) {
      return null;
    }

    return [new Date(beginning), new Date(ending)];
  }, [beginning, ending]);

  const handleFilterDate = useCallback(
    (range: RangeValue<Date>) => {
      if (!range || !range[0] || !range[1]) {
        onDateChange(undefined, undefined);
      } else {
        onDateChange(
          formatDateWithoutHour(range[0]),
          formatDateWithoutHour(range[1])
        );
      }
    },
    [onDateChange]
  );

  return (
    <div className={styles.bookingsHeader}>
      <Row gutter={16} align="middle">
        <Col flex="auto">
          <div className={styles.searchContainer}>
            <Input
              placeholder="Search..."
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              suffix={<SearchOutlined />}
              size="large"
              allowClear
            />
            <RangePicker
              value={dateRange}
              onChange={handleFilterDate}
              format="ddd DD MMM"
              ranges={{
                Today: [new Date(), new Date()],
                'This Week': [
                  startOfISOWeek(new Date()),
                  endOfISOWeek(new Date()),
                ],
              }}
              size="large"
              allowClear
            />
          </div>
        </Col>
        {onDownloadCsv && (
          <Col>
            <Button
              onClick={onDownloadCsv}
              loading={isDownloading}
              size="large"
            >
              Download .csv
            </Button>
          </Col>
        )}
        {pagination && (
          <Col>
            <Pagination
              current={pagination.pageNumber}
              pageSize={pagination.pageSize}
              total={pagination.itemCount}
              showSizeChanger={false}
              onChange={onPageChange}
              simple
            />
          </Col>
        )}
      </Row>
    </div>
  );
};
